// Tính tiền voucher cho order item — quy tắc giống bill item (BillInfo.reCalVoucherBillItem).
// Xem docs/superpowers/plans/2026-09-15-order-voucher-preorder.md §0.5.
import { ITEM_TYPE, DISCOUNT_AMOUNT_TYPE } from '@/utils/enum';

const MAX_PARSE_DEPTH = 3;

export const parseCumluativeValues = (value) => {
  let parsed = value;
  for (let i = 0; i < MAX_PARSE_DEPTH && typeof parsed === 'string'; i += 1) {
    if (!parsed) return [];
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      return [];
    }
  }
  return Array.isArray(parsed) ? parsed : [];
};

const isVoucherEligibleItem = (item) =>
  Boolean(item) && Number(item.orderItemType) === ITEM_TYPE.MAIN;

const findCumulativePercent = (tiers, totalQuantity) => {
  const tier = tiers.find(
    (item) =>
      Number(item.quantityFrom) <= totalQuantity &&
      totalQuantity <= Number(item.quantityTo),
  );
  return tier ? Number(tier.cumulativeValue) || 0 : 0;
};

// Voucher đã lưu (gateway/DB) không có voucherType → giữ nguyên số tiền đã lưu,
// không bao giờ tính lại (kể cả voucher cộng dồn đã lưu).
const isStoredVoucher = (voucher) => !voucher.voucherType;

// Voucher trong phiên (được thêm qua modal, có voucherType) → luôn tính lại từ
// config (voucherType/value/maxValue/cumluativeValues), không dựa vào voucherAmount cũ.
const calcSessionVoucherRawAmount = (voucher, unitPrice, quantity, quantityByVoucherId) => {
  const tiers = parseCumluativeValues(voucher.cumluativeValues);
  if (tiers.length) {
    const totalQuantity = quantityByVoucherId[voucher.voucherId] || 0;
    return (
      Math.floor((unitPrice * findCumulativePercent(tiers, totalQuantity)) / 100) * quantity
    );
  }
  if (Number(voucher.voucherType) === DISCOUNT_AMOUNT_TYPE.MONEY) {
    return Math.floor(Number(voucher.value) || 0);
  }
  // Mirror backend calVoucherValue (voucher.service.js): discount < maxValue ? discount : maxValue.
  return Math.floor(
    Math.min((unitPrice * (Number(voucher.value) || 0)) / 100, Number(voucher.maxValue) || 0),
  );
};

export const buildOrderVoucher = (voucherItem) => ({
  voucherId: voucherItem.voucherId,
  voucherDetailId: voucherItem.voucherDetailId,
  voucherCode: voucherItem.voucherCode,
  voucherTypeDiscount: DISCOUNT_AMOUNT_TYPE.MONEY,
  voucherAmount: Math.floor(Number(voucherItem.voucherValue) || 0),
  cumluativeValues: parseCumluativeValues(voucherItem.cumluativeValues),
  // config tạm để tính lại khi đổi giá; backend bỏ qua các field này
  voucherType: voucherItem.voucherType,
  value: voucherItem.value,
  maxValue: voucherItem.maxValue,
});

export const recalcOrderVoucherAmounts = (orderItems) => {
  const items = orderItems || [];
  const mainItems = items.filter(isVoucherEligibleItem);
  const quantityByVoucherId = mainItems.reduce((result, item) => {
    (item.vouchers || []).forEach((voucher) => {
      result[voucher.voucherId] =
        (result[voucher.voucherId] || 0) + (Number(item.quantity) || 0);
    });
    return result;
  }, {});

  mainItems.forEach((item) => {
    if (!item.vouchers || !item.vouchers.length) return;
    const unitPrice = Number(item.sellingPriceMask) || 0;
    const quantity = Number(item.quantity) || 0;
    const discountAmount = Number(item.discountAmount) || 0;
    let remaining = Math.max(0, unitPrice * quantity - discountAmount);

    // Trừ trước ngân sách của các voucher đã lưu, không đụng vào chúng.
    item.vouchers.forEach((voucher) => {
      if (isStoredVoucher(voucher)) {
        remaining = Math.max(0, remaining - (Number(voucher.voucherAmount) || 0));
      }
    });

    // Chỉ tính lại voucher trong phiên, theo đúng thứ tự trong mảng.
    item.vouchers.forEach((voucher) => {
      if (isStoredVoucher(voucher)) return;
      const rawAmount = calcSessionVoucherRawAmount(
        voucher,
        unitPrice,
        quantity,
        quantityByVoucherId,
      );
      const voucherAmount = Math.max(0, Math.min(rawAmount, remaining));
      remaining -= voucherAmount;
      voucher.voucherAmount = voucherAmount;
      voucher.voucherTypeDiscount = DISCOUNT_AMOUNT_TYPE.MONEY;
    });
  });
  return items;
};
